import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import ColorWrapper from '@styles/colors';
import { ScrollView, StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { navigationLevels } from '@v2/pages/machines/common/constants';
import Selectors from '@styles/selectors';
import { scale } from '@components/frame';
import { font } from '@styles/fonts';
import i18n from 'i18n-js';
import { useFocusEffect, useRoute } from '@react-navigation/native';
import { useHandleNavigationMachinesHeader } from '@v2/pages/machines/hooks/useHandleNavigationMachineHeader';
import { isPhone, isPhoneNative, isWeb, searchDisplayMode } from '@helpers';
import LevelSelectorWrapper from '@components/machines/components/header/components/levelSelector';
import { DetailsScreenLevelTitle, LevelSubScreen, ModalType, Perspective } from '@v2/pages/common/enums';
import { connect, useSelector } from 'react-redux';
import { useHandleNavigationContextHeader } from '@v2/pages/common/hooks/useHandleNavigationContextHeader';
import { ColorFunction } from '@styles/colors/types';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { useLevelsSubMenu } from '@v2/pages/common/hooks/useLevelsSubMenu';
import {
  DETAILS_SCREEN_TAB_NAME,
  HEADER_MOBILE_GAP_BETWEEN_LEVELS,
  pluralTabNameMap,
  pluralTitleRegExp,
  SCREEN_PADDING_HORIZONTAL_FOR_ITEM,
  singularTabNameMap,
  TEST_IDS,
} from '../../constants/base.constants';
import {
  addToPerspectiveTabHistory,
  addToTabHistory,
  setDisplayMode,
  addToPrevPerspectiveNameValueHistory,
} from '../../../../../../reducers/details';
import DocumentsTools from './DocumentsTools';
import FavoriteButton from '@components/common/favoriteButton';
import { DETAILS_SCREEN_TAB_NAME_TO_SCOPE_ID } from '@components/imageHandling/common';
import { ReducerEntityWithLoadingState } from '@v2/pages/common/interfaces';
import { ProductionBatchStatistics } from '@interfaces/search';
import { InfoScreen } from '@interfaces/base';
import { Machine } from '@interfaces/machines';
import { AddToTabHistory, ReplaceElementInTabHistory } from '@interfaces/methods';
import { NcFileBatchEventType as ENCFileBatchEvents } from '@v2/pages/common/enums';
import { useHeaderConfigPluralCheck } from '@v2/pages/common/hooks/useHeaderConfigPluralCheck';
import DocTypeButton from '@v2/components/imageHandling/components/Header/components/DocTypeButton';
import { selectCurrentContextImagesDocuments } from '@reducers/v2/imageHandlingV2/imageHandlingV2.slice';

interface NavLinkProps {
  title: string;
  selected: boolean;
  onPress: (title: string) => void;
  preselected?: boolean;
  testID?: string;
}

interface PerformanceProps {
  rowStyles: StyleProp<ViewStyle>;
  borderStyles: StyleProp<ViewStyle>;
}

const NavLink: FC<NavLinkProps> = ({ title, selected, onPress, preselected, testID }) => {
  return (
    <Selectors
      {...{
        preselected,
        scale,
        font,
        selected,
        variant: 'L',
        text: i18n.t(title),
        onPress,
        testID,
      }}
    />
  );
};

const Performance: FC<PerformanceProps> = ({ rowStyles, borderStyles }) => {
  const { timePeriod: _timePeriod } = useHandleParams();

  const { updateTimePeriod, activePerformanceSubScreenName } = useHandleNavigationMachinesHeader();
  const { handleNavigateNestedPerformanceSubScreen } = useHandleNavigationContextHeader();

  return (
    <>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={borderStyles} contentContainerStyle={rowStyles}>
        {Object.values(navigationLevels.contextHeaderMetricTypes).map((title) => (
          <NavLink
            key={title}
            title={title}
            selected={title === activePerformanceSubScreenName}
            onPress={() => handleNavigateNestedPerformanceSubScreen(title)}
          />
        ))}
      </ScrollView>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={[borderStyles]} contentContainerStyle={rowStyles}>
        {Object.entries(navigationLevels.timePeriod).map(([timePeriod, title]) => {
          const timePeriodTestIDs: Record<string, string> = {
            day: TEST_IDS.contextHeader.timePeriodDay,
            week: TEST_IDS.contextHeader.timePeriodWeek,
            month: TEST_IDS.contextHeader.timePeriodMonth,
            year: TEST_IDS.contextHeader.timePeriodYear,
          };
          return (
            <NavLink
              key={timePeriod}
              title={title}
              selected={_timePeriod === timePeriod}
              onPress={() => updateTimePeriod(timePeriod)}
              testID={timePeriodTestIDs[timePeriod]}
            />
          );
        })}
      </ScrollView>
    </>
  );
};

const NcFileBatchEventType: FC<Pick<PerformanceProps, 'rowStyles' | 'borderStyles'>> = ({ rowStyles, borderStyles }) => {
  const { activeNcFileBatchEventsSubScreenName, handleNavigateNcFileBatchEvents } = useHandleNavigationContextHeader();

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      style={[borderStyles]}
      contentContainerStyle={[rowStyles, isPhone && { paddingBottom: scale(HEADER_MOBILE_GAP_BETWEEN_LEVELS) }]}
    >
      {Object.values(ENCFileBatchEvents).map((title) => {
        return (
          <NavLink
            key={title}
            title={title}
            selected={title === activeNcFileBatchEventsSubScreenName}
            onPress={() => handleNavigateNcFileBatchEvents(title)}
          />
        );
      })}
    </ScrollView>
  );
};

export interface HeaderConfig {
  batch: string | number;
  ncFile: string | number;
  ncFileId: string | number;
  ncFileStatus: string;
  productionOrder: string;
  part: {
    name: string;
    id: string | number;
  };
  job: string | number;
  tools: string | number;
}

interface ContextHeaderProps {
  panelViewMode: boolean;
  C: ColorFunction;
  ncFileId: number;
  partId: number;
  jobId: number;
  batch: ReducerEntityWithLoadingState<ProductionBatchStatistics | null>;
  perspectiveTabHistory: InfoScreen[];
  addToPerspectiveTabHistory: AddToTabHistory;
  addToTabHistory: AddToTabHistory;
  isWithERP: boolean | null;
  selectedItem: Machine;
  headerConfig: HeaderConfig;
  setDisplayMode: (mode: DisplayMode) => void;
  addToPrevPerspectiveNameValueHistory: (nameValue: { name: string; value: string }) => void;
}

export type DisplayMode = (typeof searchDisplayMode)[keyof typeof searchDisplayMode];

const ContextHeader: FC<ContextHeaderProps> = ({
  C,
  panelViewMode,
  selectedItem,
  headerConfig,
  isWithERP,
  setDisplayMode: setDisplayModeInReducer,
  perspectiveTabHistory,
  addToPrevPerspectiveNameValueHistory,
  addToPerspectiveTabHistory,
  addToTabHistory,
  ncFileId,
  partId,
  jobId,
  batch,
}) => {
  const route = useRoute();

  const customAnchorRef = useRef(null);

  const {
    handleNavigateDetailsLevel,
    handleNavigateDetailsLevelSubScreen,
    activeLevelSubScreenName,
    screenConfig,
    activeLevelScreenName,
    shouldDisplayModeSelector,
    shouldDisplayNcFileBatchEventsSubLevelMenu,
    checkWithPerspectiveBadge,
    onPerspectiveBadgePress,
  } = useHandleNavigationContextHeader();

  const images = useSelector(selectCurrentContextImagesDocuments);

  const defaultDisplayMode = searchDisplayMode.gallery;

  const [displayMode, _setDisplayMode] = useState<DisplayMode>(defaultDisplayMode);

  const setDisplayMode = async (mode: DisplayMode) => {
    _setDisplayMode(mode);
    setDisplayModeInReducer(mode);
  };

  useFocusEffect(
    useCallback(() => {
      setDisplayMode(displayMode);
    }, [displayMode])
  );

  const {
    ncFileLevelSubScreens,
    batchLevelSubScreens,
    jobsLevelSubScreens,
    partLevelSubScreens,
    productionOrderLevelSubScreens,
    toolLevelSubScreens,
  } = useLevelsSubMenu();

  const rowStyles: StyleProp<ViewStyle> = {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: scale(HEADER_MOBILE_GAP_BETWEEN_LEVELS * 2),
    marginHorizontal: 'auto',
    ...(isWeb ? { width: '100%' } : {}),
    ...(isPhone ? { paddingBottom: scale(HEADER_MOBILE_GAP_BETWEEN_LEVELS) } : {}),
  };

  const borderStyles = { borderTopColor: C('Hairline/Light/No trans'), borderTopWidth: isPhone ? StyleSheet.hairlineWidth : 0 };

  const perspective = route.params?.perspective;

  const { batchPlural, jobsPlural, ncFilePlural, partPlural, productionOrdersPlural, toolsPlural } = useHeaderConfigPluralCheck();

  const allPossibleLevelTabs = [
    {
      name: perspective === Perspective.PART ? singularTabNameMap[DETAILS_SCREEN_TAB_NAME.Part] : DETAILS_SCREEN_TAB_NAME.Part,
      key: screenConfig?.levelScreens?.PART,
      value: headerConfig?.part?.name,
      onPress: () => {
        handleNavigateDetailsLevel({ levelScreen: screenConfig?.levelScreens?.PART });
      },
      shouldDisplayModalTriggerButton: !isWithERP && !partPlural,
      modalType: ModalType.PART,
      preselected: perspective === Perspective.PART,
      plural: partPlural,
      children: !partPlural ? partLevelSubScreens : [],
      perspective,
      perspectiveToGo: Perspective.PART,
      testID: TEST_IDS.detailsPage.levelTab(Perspective.PART),
      perspectiveBadgeTestID: TEST_IDS.detailsPage.levelTabPerspectiveBadge(Perspective.PART),
    },
    {
      name: perspective === Perspective.ORDER ? singularTabNameMap[DETAILS_SCREEN_TAB_NAME.Order] : DETAILS_SCREEN_TAB_NAME.Order,
      key: screenConfig?.levelScreens?.ORDER,
      value: headerConfig?.productionOrder,
      shouldDisplayModalTriggerButton: false,
      modalType: ModalType.ORDER,
      onPress: () => {
        handleNavigateDetailsLevel({ levelScreen: screenConfig?.levelScreens?.ORDER });
      },
      preselected: perspective === Perspective.ORDER,
      plural: productionOrdersPlural,
      children: !productionOrdersPlural ? productionOrderLevelSubScreens : [],
      perspective,
      perspectiveToGo: Perspective.ORDER,
      testID: TEST_IDS.detailsPage.levelTab(Perspective.ORDER),
      perspectiveBadgeTestID: TEST_IDS.detailsPage.levelTabPerspectiveBadge(Perspective.ORDER),
    },
    {
      name: perspective === Perspective.JOBS ? singularTabNameMap[DETAILS_SCREEN_TAB_NAME.Jobs] : DETAILS_SCREEN_TAB_NAME.Jobs,
      key: screenConfig?.levelScreens?.JOBS,
      value: headerConfig?.job,
      shouldDisplayModalTriggerButton: false,
      modalType: ModalType.JOBS,
      onPress: () => {
        handleNavigateDetailsLevel({ levelScreen: screenConfig?.levelScreens?.JOBS });
      },
      preselected: perspective === Perspective.JOBS,
      plural: jobsPlural,
      children: !jobsPlural ? jobsLevelSubScreens : [],
      perspective,
      perspectiveToGo: Perspective.JOBS,
      testID: TEST_IDS.detailsPage.levelTab(Perspective.JOBS),
      perspectiveBadgeTestID: TEST_IDS.detailsPage.levelTabPerspectiveBadge(Perspective.JOBS),
    },
    {
      name: perspective === Perspective.BATCH ? singularTabNameMap[DETAILS_SCREEN_TAB_NAME.Batch] : DETAILS_SCREEN_TAB_NAME.Batch,
      key: screenConfig?.levelScreens?.BATCH,
      value: headerConfig?.batch,
      shouldDisplayModalTriggerButton: false,
      modalType: ModalType.BATCH,
      onPress: () => {
        handleNavigateDetailsLevel({ levelScreen: screenConfig?.levelScreens?.BATCH });
      },
      preselected: perspective === Perspective.BATCH,
      plural: batchPlural,
      children: !batchPlural ? batchLevelSubScreens : [],
      perspective,
      perspectiveToGo: Perspective.BATCH,
      testID: TEST_IDS.detailsPage.levelTab(Perspective.BATCH),
      perspectiveBadgeTestID: TEST_IDS.detailsPage.levelTabPerspectiveBadge(Perspective.BATCH),
    },
    {
      name: perspective === Perspective.NC_FILE ? singularTabNameMap[DETAILS_SCREEN_TAB_NAME.NCFile] : DETAILS_SCREEN_TAB_NAME.NCFile,
      key: screenConfig?.levelScreens?.NC_FILE,
      value: headerConfig?.ncFile,
      shouldDisplayModalTriggerButton: !ncFilePlural,
      modalType: ModalType.NC_FILE,
      onPress: () => {
        handleNavigateDetailsLevel({ levelScreen: screenConfig?.levelScreens?.NC_FILE });
      },
      preselected: perspective === Perspective.NC_FILE,
      plural: ncFilePlural,
      children: ncFileLevelSubScreens,
      perspective,
      perspectiveToGo: Perspective.NC_FILE,
      testID: TEST_IDS.detailsPage.levelTab(Perspective.NC_FILE),
      perspectiveBadgeTestID: TEST_IDS.detailsPage.levelTabPerspectiveBadge(Perspective.NC_FILE),
    },
    {
      name: perspective === Perspective.TOOLS ? singularTabNameMap[DETAILS_SCREEN_TAB_NAME.Tools] : DETAILS_SCREEN_TAB_NAME.Tools,
      key: screenConfig?.levelScreens?.TOOLS,
      value: headerConfig?.tools,
      shouldDisplayModalTriggerButton: true,
      modalType: ModalType.TOOLS,
      onPress: () => {
        handleNavigateDetailsLevel({ levelScreen: screenConfig?.levelScreens?.TOOLS });
      },
      preselected: perspective === Perspective.TOOLS,
      plural: toolsPlural,
      children: !toolsPlural ? toolLevelSubScreens : [],
      perspective,
      perspectiveToGo: Perspective.TOOLS,
      testID: TEST_IDS.detailsPage.levelTab(Perspective.TOOLS),
      perspectiveBadgeTestID: TEST_IDS.detailsPage.levelTabPerspectiveBadge(Perspective.TOOLS),
    },
  ];

  const levelTabs = allPossibleLevelTabs.filter((tab) => tab.key);

  const activeLevelTab = useMemo(() => {
    return levelTabs.find((tab) => route.name.includes(tab.key));
  }, [route.name, headerConfig, isWithERP]);

  useFocusEffect(
    useCallback(() => {
      const lastInPerspectiveTabHistory: InfoScreen = perspectiveTabHistory?.at(-1);

      if (activeLevelTab) {
        const newTab = {
          ...activeLevelTab,
          mainScreen: screenConfig.mainScreen,
          route,
        };

        addToTabHistory(newTab);

        // Uncomment if we need to track screens when perspective doesn't change to return back to last screen in prev perspective
        // if (lastInPerspectiveTabHistory && lastInPerspectiveTabHistory.perspective === perspective) {
        //   replaceElementPerspectiveTabHistory({ newTab });
        //   return;
        // }

        if (!lastInPerspectiveTabHistory || lastInPerspectiveTabHistory.perspective !== perspective) {
          addToPrevPerspectiveNameValueHistory({
            name: newTab?.name || '',
            value: newTab?.value?.toString() || '',
          });
          addToPerspectiveTabHistory(newTab);
          return;
        }
      }
    }, [activeLevelTab])
  );

  const isWithERPLoaded = isWithERP !== null;
  const shouldDisplayLevelTabs = isWithERPLoaded;
  const levelSelectorBg = !isWeb ? 'Background/Light/Field on Panel' : 'Background/Light/Field';

  const scopeId = activeLevelTab && activeLevelTab.name ? DETAILS_SCREEN_TAB_NAME_TO_SCOPE_ID[activeLevelTab?.name] : null;

  const scopeReferenceName = activeLevelTab && activeLevelTab.value ? String(activeLevelTab.value) : null;

  const scopeReferenceIdConfig = {
    [DETAILS_SCREEN_TAB_NAME.NCFile]: ncFileId,
    [DETAILS_SCREEN_TAB_NAME.Part]: partId,
    [DETAILS_SCREEN_TAB_NAME.Jobs]: jobId,
    [DETAILS_SCREEN_TAB_NAME.Batch]: batch?.data?.productionBatchId,
  };

  const scopeReferenceId = activeLevelTab && activeLevelTab.name ? scopeReferenceIdConfig[activeLevelTab.name] : null;

  const shouldDisplayLevelSubMenu =
    !!activeLevelTab &&
    (activeLevelTab.children.length > 1 || (activeLevelTab.children.length === 1 && activeLevelTab.children[0] !== LevelSubScreen.GENERAL));

  const shouldDisplayGalleryModeTools = shouldDisplayModeSelector && displayMode === searchDisplayMode.gallery;

  return (
    <View style={{ gap: scale(HEADER_MOBILE_GAP_BETWEEN_LEVELS) }}>
      {shouldDisplayLevelTabs && (
        <View style={{ gap: scale(HEADER_MOBILE_GAP_BETWEEN_LEVELS), paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL_FOR_ITEM) }}>
          {!isPhone && (
            <LevelSelectorWrapper
              {...{
                levelTabs,
                panelViewMode,
                scale,
                font,
                isNCFile: !!selectedItem?.machineStatus?.ncFile,
                blur: isPhone,
                big: isWeb,
                selectedItem,
                noMachine: true,
                valueFont: 'Footnote',
                itemFont: 'Button 1',
                levelSelectorBg: levelSelectorBg,
                itemBG: levelSelectorBg,
                withShadow: true,
                checkWithPerspectiveBadge,
                onPerspectiveBadgePress,
              }}
            />
          )}
          {isPhone && (
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={rowStyles}>
              {Object.entries(screenConfig?.levelScreens ?? []).map(([key, title]) => {
                const screenName = DetailsScreenLevelTitle[key];
                const isPlural = levelTabs.find((tab) => tab.key === title && tab.plural);
                const _screenName = isPlural ? pluralTabNameMap[screenName] : singularTabNameMap[screenName];

                return (
                  <View key={title}>
                    <NavLink
                      title={_screenName}
                      selected={title === activeLevelScreenName}
                      onPress={() => handleNavigateDetailsLevel({ levelScreen: title })}
                      preselected={screenName?.toLowerCase() === perspective}
                    />
                  </View>
                );
              })}
            </ScrollView>
          )}

          {shouldDisplayLevelSubMenu && (
            <>
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: isPhone ? 'space-between' : 'center',
                  alignItems: 'center',
                }}
              >
                {/* {shouldDisplayGalleryModeTools && !isPhone && (
                  <View
                    style={{
                      position: 'absolute',
                      left: 0,
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      paddingTop: scale(HEADER_MOBILE_GAP_BETWEEN_LEVELS * 2),
                      gap: scale(18),
                      zIndex: 10000000,
                    }}
                  >
                    <FavoriteButton {...{ C, iconColor: 'Link/Light/Primary', scopeId, scopeReferenceName, scopeReferenceId }} />
                    <AttachButton {...{ C }} />
                  </View>
                )} */}
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  style={borderStyles}
                  contentContainerStyle={[rowStyles, { position: 'relative', alignItems: 'flex-start' }]}
                >
                  {activeLevelTab.children.map((level) => (
                    <NavLink
                      key={level}
                      title={level}
                      selected={level === activeLevelSubScreenName}
                      onPress={() => handleNavigateDetailsLevelSubScreen(level)}
                    />
                  ))}
                </ScrollView>
                {shouldDisplayModeSelector && !isPhone && (
                  <View style={{ position: 'absolute', right: 0 }}>
                    <DocumentsTools
                      {...{
                        C,
                        activeTabName: activeLevelTab?.name,
                        customAnchorRef,
                        displayMode,
                        setDisplayMode,
                        modeSelectorContainerStyle: {
                          transform: [{ translateY: scale(1) }],
                          alignItems: 'center',
                        },
                        containerStyle: { alignItems: 'center', paddingTop: scale(HEADER_MOBILE_GAP_BETWEEN_LEVELS * 2) },
                      }}
                    />
                  </View>
                )}
              </View>
              {shouldDisplayModeSelector && isPhoneNative && (
                <View>
                  <View
                    style={[
                      borderStyles,
                      rowStyles,
                      {
                        flexDirection: 'row',
                        alignItems: 'center',
                        width: '100%',
                        justifyContent: 'space-between',
                        paddingBottom: 0,
                        paddingRight: scale(6),
                      },
                    ]}
                  >
                    {shouldDisplayGalleryModeTools && (
                      <FavoriteButton
                        {...{
                          C,
                          scopeId,
                          scopeReferenceName,
                          scopeReferenceId,
                          iconColor: 'Button/Light/Icon PhoneTab',
                          style: { transform: [{ translateX: scale(-7) }] },
                        }}
                      />
                    )}
                    <DocumentsTools
                      {...{
                        C,
                        activeTabName: activeLevelTab?.name,
                        customAnchorRef,
                        displayMode,
                        setDisplayMode,
                        containerStyle: { alignItems: 'center', marginLeft: 'auto' },
                      }}
                    />
                  </View>
                  {shouldDisplayGalleryModeTools && (
                    <View style={{ height: scale(40), marginVertical: 'auto', justifyContent: 'center' }}>
                      <DocTypeButton C={C} images={images} />
                    </View>
                  )}
                </View>
              )}
              {activeLevelSubScreenName === LevelSubScreen.PERFORMANCE && <Performance rowStyles={rowStyles} borderStyles={borderStyles} />}
              {shouldDisplayNcFileBatchEventsSubLevelMenu && <NcFileBatchEventType borderStyles={borderStyles} rowStyles={rowStyles} />}
            </>
          )}
        </View>
      )}
    </View>
  );
};

const mapStateToProps = ({
  machines: { selectedItem },
  details: { headerConfig, ncFileId, partId, jobId, perspectiveTabHistory },
  batch: { batch },
  membership: { isWithERP },
}) => ({
  perspectiveTabHistory,
  selectedItem,
  headerConfig,
  isWithERP,
  ncFileId,
  partId,
  jobId,
  batch,
});

export default connect(mapStateToProps, {
  setDisplayMode,
  addToPerspectiveTabHistory,
  addToTabHistory,
  addToPrevPerspectiveNameValueHistory,
})(ColorWrapper(ContextHeader));
